import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Result,
  Segmented,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import { ArrowLeftOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { decideOffer, errorText, getOffer, openPortalFile } from "./api";
import { SignatureForm } from "../../../serviceProvider/components/workReports/SignDialog";
import {
  formatDateTime,
  formatDay,
  formatEuro,
  formatQuantity
} from "../../../serviceProvider/components/workReports/format";
import { changeOrderState, offerStatus } from "./labels";
import "../../../serviceProvider/components/workReports/workReports.css";
import "./portal.css";

const { Paragraph, Text, Title } = Typography;

/** One offer: read it, open the PDF, accept it with a signature or decline it with a reason. */
const PortalOfferDetail = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const [state, setState] = useState({ loading: true });
  const [choice, setChoice] = useState("accepted");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    getOffer(id)
      .then(res => setState({ loading: false, offer: res.data.data }))
      .catch(err => setState({ loading: false, failed: errorText(err, t("Offer not found")) }));
  }, [id, t]);

  const send = async body => {
    setBusy(true);
    setError("");
    try {
      const res = await decideOffer(id, { ...body, contentHash: state.offer.contentHash });
      setState({ loading: false, offer: res.data.data });
      setDone(body.decision);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(errorText(err, t("Your answer could not be saved. Please try again.")));
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    try {
      await openPortalFile(`/offers/${id}/pdf`, `Angebot-${state.offer.number || id}.pdf`);
    } catch (err) {
      setError(errorText(err, t("The PDF could not be created.")));
    }
  };

  if (state.loading) {
    return (
      <div className="portal-page">
        <Card>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }
  if (state.failed) {
    return (
      <div className="portal-page">
        <Result
          status="404"
          title={state.failed}
          extra={<Link to="/client/offers">{t("Back to the offers")}</Link>}
        />
      </div>
    );
  }

  const o = state.offer;
  const tag = offerStatus(t, o.status);

  return (
    <div className="portal-page">
      <Link className="portal-back" to="/client/offers">
        <ArrowLeftOutlined aria-hidden="true" /> {t("Offers")}
      </Link>

      {done && (
        <Result
          status={done === "accepted" ? "success" : "info"}
          title={
            done === "accepted"
              ? t("Thank you for your order. We will be in touch.")
              : t("Thank you, we have received your answer.")
          }
        />
      )}

      <Card className="portal-card portal-gap">
        <div className="portal-doc-head">
          <div>
            <Title level={4}>{o.title}</Title>
            <Text type="secondary">
              {[o.number, formatDay(o.date)].filter(Boolean).join(" · ")}
            </Text>
          </div>
          <Space wrap>
            <Tag color={tag.color}>{tag.text}</Tag>
            <Button icon={<FilePdfOutlined />} onClick={openPdf}>
              PDF
            </Button>
          </Space>
        </div>
        {(o.objectAddress || o.project) && (
          <Descriptions size="small" column={1} className="portal-gap-top">
            {o.project && (
              <Descriptions.Item label={t("Project")}>{o.project.name}</Descriptions.Item>
            )}
            {o.objectAddress && (
              <Descriptions.Item label={t("Site")}>{o.objectAddress}</Descriptions.Item>
            )}
            {o.validUntil && (
              <Descriptions.Item label={t("Valid until")}>
                {formatDay(o.validUntil)}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
        {o.introText && <Paragraph className="portal-text">{o.introText}</Paragraph>}

        <ol className="portal-positions">
          {o.positions.map(p => (
            <li key={p._id} className={p.counts ? "" : "portal-positions__optional"}>
              <div className="portal-positions__head">
                <span className="portal-positions__text">
                  {p.oz ? <Text type="secondary">{p.oz} </Text> : null}
                  <strong>{p.description}</strong>
                </span>
                <strong className="portal-num">{formatEuro(p.net, lng)}</strong>
              </div>
              {p.longText && <div className="portal-positions__long">{p.longText}</div>}
              <Text type="secondary" className="portal-num">
                {formatQuantity(p.quantity, lng)} {p.unit} × {formatEuro(p.unitPrice, lng)}
                {!p.counts && ` · ${t("optional, not in the total")}`}
              </Text>
            </li>
          ))}
        </ol>

        <dl className="portal-totals">
          <dt>{t("Net")}</dt>
          <dd>{formatEuro(o.totals.net, lng)}</dd>
          <dt>{t("VAT {{rate}} %", { rate: o.totals.rate })}</dt>
          <dd>{formatEuro(o.totals.tax, lng)}</dd>
          <dt className="portal-totals__gross">{t("Total")}</dt>
          <dd className="portal-totals__gross">{formatEuro(o.totals.gross, lng)}</dd>
        </dl>
        {o.closingText && <Paragraph className="portal-text">{o.closingText}</Paragraph>}
      </Card>

      {o.changeOrders?.length > 0 && (
        <Card title={t("Change orders")} className="portal-card portal-gap">
          {o.changeOrders.map(co => {
            const coTag = changeOrderState(t, co.state);
            return (
              <Link
                key={co._id}
                className="portal-row"
                to={`/client/offers/change-orders/${co._id}`}
              >
                <span className="portal-row__main">
                  <strong>
                    {co.number} – {co.title}
                  </strong>
                </span>
                <Tag color={coTag.color}>{coTag.text}</Tag>
              </Link>
            );
          })}
        </Card>
      )}

      {o.decision && (
        <Card title={t("Your answer")} className="portal-card portal-gap">
          <Paragraph>
            {o.decision.decision === "accepted"
              ? t("Accepted by {{name}} on {{date}}.", {
                  name: o.decision.name,
                  date: formatDateTime(o.decision.at, lng)
                })
              : t("Declined on {{date}}.", { date: formatDateTime(o.decision.at, lng) })}
          </Paragraph>
          {o.decision.reason && <Paragraph type="secondary">{o.decision.reason}</Paragraph>}
          {o.decision.image && (
            <img className="portal-signature" src={o.decision.image} alt={t("Signature")} />
          )}
          <Text type="secondary" className="portal-hash">
            SHA-256 {o.decision.contentHash}
          </Text>
        </Card>
      )}

      {o.canDecide && (
        <Card title={t("Your decision")} className="portal-card">
          {error && <Alert className="portal-gap" type="error" showIcon message={error} />}
          <Segmented
            block
            value={choice}
            onChange={setChoice}
            options={[
              { value: "accepted", label: t("Accept") },
              { value: "rejected", label: t("Decline") }
            ]}
            className="portal-gap"
          />
          {choice === "accepted" ? (
            <>
              <Paragraph type="secondary">
                {t(
                  "With your signature you order the work in this offer at the price shown ({{amount}}).",
                  {
                    amount: formatEuro(o.totals.gross, lng)
                  }
                )}
              </Paragraph>
              <div className="work-reports-page">
                <SignatureForm
                  showCopy={false}
                  showLocation={false}
                  submitting={busy}
                  onSubmit={values =>
                    send({ decision: "accepted", name: values.name, image: values.image })
                  }
                />
              </div>
            </>
          ) : (
            <>
              <Paragraph type="secondary">
                {t("Please tell us briefly why. It helps us to improve.")}
              </Paragraph>
              <Input.TextArea
                rows={4}
                maxLength={2000}
                value={reason}
                onChange={event => setReason(event.target.value)}
                aria-label={t("Reason")}
              />
              <Button
                className="portal-gap-top"
                danger
                block
                size="large"
                loading={busy}
                disabled={reason.trim().length < 3}
                onClick={() => send({ decision: "rejected", reason })}
              >
                {t("Decline offer")}
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default PortalOfferDetail;
